// src/components/doctor-profile/ProfileHeaderCard.jsx
import React from "react";
import {
  Activity,
  Shield,
  User,
  Camera,
  Stethoscope,
  MapPin,
  Phone,
  Mail,
  Award,
  Calendar,
  MessageCircle,
  ThumbsUp,
  Star,
} from "lucide-react";
import { styles as baseStyles } from "../../style/DoctorProfile.styles";

/** Mobile-first, blended hero header — props unchanged */
const ProfileHeaderCard = ({
  doctor,
  reviewStats,
  experienceYears,
  isOwnProfile,
  uploadingPhoto,
  onUploadPhoto,
  onBook,
  onMessage,
  styles = baseStyles,
}) => {
  const brand = {
    primary: styles?.brandPrimary || "#0ea5e9",
    secondary: styles?.brandSecondary || "#6366f1",
    dark: styles?.brandDark || "#0f172a",
    amber: "#fbbf24",
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (f) onUploadPhoto?.(f);
  };

  const Stars = ({ rating = 0, size = 16 }) => (
    <div style={{ display: "flex", gap: 2 }}>
      {[1,2,3,4,5].map(i => (
        <Star
          key={i}
          style={{
            width: size, height: size,
            color: i <= rating ? brand.amber : "#e5e7eb",
            fill: i <= rating ? brand.amber : "transparent"
          }}
        />
      ))}
    </div>
  );

  return (
    <div className="phc-wrap">
      <style>{`
        :root{
          --phc-primary:${brand.primary};
          --phc-secondary:${brand.secondary};
          --phc-dark:${brand.dark};
        }
        .phc-wrap{
          border-radius:20px;
          padding:8px;
          /* Softer blend on small screens */
          background:
            radial-gradient(700px 240px at 50% -20%, var(--phc-primary)10%, transparent 65%),
            radial-gradient(500px 200px at 90% 0%, var(--phc-secondary)0d, transparent 55%);
        }
        .phc-card{
          background:rgba(255,255,255,0.86);
          border:1px solid rgba(255,255,255,0.55);
          backdrop-filter:blur(8px);
          -webkit-backdrop-filter:blur(8px);
          border-radius:16px;
          padding:12px;              /* compact mobile padding */
          box-shadow:0 18px 40px -18px rgba(2,6,23,0.25);
        }
        .phc-top{
          display:flex; align-items:center; gap:8px; margin-bottom:6px;
        }
        .phc-dot{
          width:24px; height:24px; border-radius:9px;
          background:linear-gradient(135deg, var(--phc-primary), var(--phc-secondary));
          display:flex; align-items:center; justify-content:center; color:#fff;
          box-shadow:0 6px 14px var(--phc-secondary)55;
          flex:0 0 auto;
        }

        /* GRID */
        .phc-grid{ display:grid; grid-template-columns:1fr; gap:12px; align-items:center; }
        .phc-left{ display:flex; justify-content:center; }
        .phc-right{ display:grid; gap:8px; text-align:center; }

        /* AVATAR */
        .phc-avatar{
          position:relative; width:80px; height:80px; border-radius:999px; padding:2px;
          background:conic-gradient(from 220deg, var(--phc-primary), var(--phc-secondary), var(--phc-primary));
          box-shadow:0 8px 18px rgba(15,23,42,0.12), inset 0 1px 0 rgba(255,255,255,0.5);
          flex:0 0 auto;
        }
        .phc-in{
          width:100%; height:100%; border-radius:999px; overflow:hidden;
          background:linear-gradient(180deg,#f1f5f9,#e2e8f0);
          display:flex; align-items:center; justify-content:center;
        }
        .phc-verified{
          position:absolute; right:-4px; bottom:-4px; width:22px; height:22px; border-radius:999px;
          background:var(--phc-primary); color:#fff; display:flex; align-items:center; justify-content:center;
          border:3px solid #fff; box-shadow:0 8px 14px var(--phc-primary)55;
        }
        .phc-upload{
          position:absolute; left:-6px; bottom:-6px; width:32px; height:32px; border-radius:999px;
          border:none; color:#fff; display:flex; align-items:center; justify-content:center; cursor:pointer;
          background:linear-gradient(135deg, var(--phc-primary), var(--phc-secondary));
          box-shadow:0 8px 16px rgba(2,6,23,0.2);
          touch-action:manipulation;
        }

        /* TEXT */
        .phc-name{
          font-size:18px; line-height:1.2; font-weight:800; letter-spacing:-0.02em; color:var(--phc-dark);
          word-break:break-word;
        }
        .phc-spec{
          display:inline-flex; align-items:center; gap:6px; width:fit-content; margin:0 auto;
          font-size:13px; font-weight:600; color:var(--phc-dark);
          background:var(--phc-primary)14; border:1px solid var(--phc-primary)33; border-radius:10px; padding:6px 10px;
        }

        /* CHIPS: horizontal scroller for mobile */
        .phc-chips{
          display:flex; gap:8px; overflow:auto; -webkit-overflow-scrolling:touch; padding-bottom:2px;
          scrollbar-width:none;
        }
        .phc-chips::-webkit-scrollbar{ display:none; }
        .phc-chip{
          display:inline-flex; align-items:center; gap:6px; flex:0 0 auto;
          padding:10px 12px; min-height:40px; border-radius:10px;
          background:rgba(2,6,23,0.04); border:1px solid rgba(2,6,23,0.06);
          color:#334155; font-size:13px; font-weight:500; max-width:240px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;
        }

        .phc-exp{
          display:inline-flex; align-items:center; gap:6px; width:fit-content; margin:4px auto 0;
          padding:8px 12px; border-radius:999px; font-size:12px; font-weight:700;
          background:#10b9811a; border:1px solid #10b98144; color:#065f46;
        }

        .phc-rating{
          display:flex; align-items:center; gap:8px; width:fit-content; margin:8px auto 0;
          padding:8px 10px; border-radius:10px; background:#fbbf241a; border:1px solid #fbbf2440;
        }
        .phc-reco{
          display:inline-flex; align-items:center; gap:6px; padding:6px 10px; border-radius:16px;
          font-size:12px; font-weight:700; border:1px solid rgba(16,185,129,0.25);
          background:rgba(16,185,129,0.12); color:#065f46;
        }

        .phc-ctas{ display:grid; grid-template-columns:1fr; gap:8px; margin-top:10px; }
        .phc-btn{
          min-height:46px; padding:12px 16px; border-radius:12px; touch-action:manipulation;
          display:inline-flex; align-items:center; justify-content:center; gap:8px; font-weight:600;
          box-shadow:inset 0 1px 0 rgba(255,255,255,0.9);
        }
        .phc-btn--primary{ background:linear-gradient(135deg,var(--phc-primary),var(--phc-secondary)); color:#fff; border:none; }
        .phc-btn--ghost{ background:#fff; color:var(--phc-dark); border:1px solid rgba(15,23,42,0.08); }

        /* Upscale at 380px and 900px */
        @media (min-width:380px){
          .phc-avatar{ width:92px; height:92px; }
          .phc-name{ font-size:20px; }
        }
        @media (min-width:900px){
          .phc-card{ padding:16px; }
          .phc-grid{ grid-template-columns:auto 1fr; gap:18px; }
          .phc-right{ text-align:left; }
          .phc-name{ font-size:28px; }
          .phc-spec{ margin:0; }
          .phc-chips{ overflow:visible; flex-wrap:wrap; }
          .phc-chip{ max-width:100%; }
          .phc-rating{ margin-left:0; }
          .phc-ctas{ grid-template-columns:auto auto; }
        }
      `}</style>

      <div className="phc-card">
        <div className="phc-top">
          <div className="phc-dot" aria-hidden>
            <Activity style={{ width: 14, height: 14 }} />
          </div>
        </div>

        <div className="phc-grid">
          {/* Avatar */}
          <div className="phc-left">
            <div className="phc-avatar">
              <div className="phc-in">
                {doctor?.photoURL ? (
                  <img
                    src={doctor.photoURL}
                    alt={doctor?.name || "Doctor"}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                ) : (
                  <User style={{ width: 48, height: 48, color: "#475569" }} />
                )}
              </div>

              <div className="phc-verified" title="Verified doctor">
                <Shield style={{ width: 12, height: 12 }} />
              </div>

              {isOwnProfile && (
                <>
                  <button
                    className="phc-upload"
                    onClick={() => document.getElementById("dp-photo-upload")?.click()}
                    disabled={uploadingPhoto}
                    title="Upload profile photo"
                  >
                    {uploadingPhoto ? <div style={styles.spinner} /> : <Camera style={{ width: 16, height: 16 }} />}
                  </button>
                  <input
                    id="dp-photo-upload"
                    type="file"
                    accept="image/*"
                    style={styles.fileHiddenInput}
                    onChange={onFile}
                  />
                </>
              )}
            </div>
          </div>

          {/* Text & Actions */}
          <div className="phc-right">
            <h1 className="phc-name">{doctor?.name || "Doctor"}</h1>

            {doctor?.specialty && (
              <span className="phc-spec">
                <Stethoscope style={{ width: 16, height: 16 }} />
                {doctor.specialty}
              </span>
            )}

            {/* Horizontal chips (scroll on mobile) */}
            <div className="phc-chips">
              {doctor?.location?.address && (
                <span className="phc-chip" title={doctor.location.address}>
                  <MapPin style={{ width: 14, height: 14, flex: "0 0 auto" }} />
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                    {doctor.location.address}
                  </span>
                </span>
              )}
              {doctor?.phone && (
                <span className="phc-chip" title={doctor.phone}>
                  <Phone style={{ width: 14, height: 14, flex: "0 0 auto" }} />
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                    {doctor.phone}
                  </span>
                </span>
              )}
              {doctor?.email && (
                <span className="phc-chip" title={doctor.email}>
                  <Mail style={{ width: 14, height: 14, flex: "0 0 auto" }} />
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                    {doctor.email}
                  </span>
                </span>
              )}
            </div>

            {/* Experience */}
            {experienceYears != null && (
              <div className="phc-exp">
                <Award style={{ width: 14, height: 14 }} />
                {experienceYears} years of experience
              </div>
            )}

            {/* Ratings */}
            {reviewStats?.totalReviews > 0 && (
              <div className="phc-rating">
                <Stars rating={reviewStats.averageRating} />
                <span style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>
                  {Number(reviewStats.averageRating).toFixed(1)}
                </span>
                <span style={{ fontSize: 12, color: "#64748b", fontWeight: 500 }}>
                  ({reviewStats.totalReviews} review{reviewStats.totalReviews !== 1 ? "s" : ""})
                </span>
                {reviewStats.recommendationPercentage >= 80 && (
                  <span className="phc-reco">
                    <ThumbsUp style={{ width: 12, height: 12 }} />
                    {Math.round(reviewStats.recommendationPercentage)}% recommend
                  </span>
                )}
              </div>
            )}

            {/* CTAs (full width on mobile) */}
            <div className="phc-ctas">
              <button className="phc-btn phc-btn--primary" onClick={onBook}>
                <Calendar style={{ width: 18, height: 18 }} />
                Book Appointment
              </button>
              <button className="phc-btn phc-btn--ghost" onClick={onMessage}>
                <MessageCircle style={{ width: 18, height: 18 }} />
                Message Doctor
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Optional (unchanged signature)
export const TinyPageHeader = ({ styles = baseStyles }) => (
  <div style={styles.header}>
    <div style={styles.headerContent}>
      <div style={styles.logoContainer}>
        <Activity style={{ color: "white", width: 20, height: 20 }} />
      </div>
      <h1 style={styles.headerTitle}>Doctor Profile</h1>
    </div>
  </div>
);

export default ProfileHeaderCard;
